// src/pages/shops/CreateShopPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Store, 
  Upload, 
  X, 
  AlertCircle, 
  MapPin, 
  Phone, 
  FileText,
  DollarSign,
  Package,
  Truck,
  Check,
  Banknote,
  Percent,
  Edit3
} from 'lucide-react';
import { createShop, checkOrderPrefix } from '../../services/api/shops';
import { getShippingZones } from '../../services/api/shipping';
import { getMyUsage } from '../../services/api/subscription';
import QuotaWarning from '../../components/common/Subscription/QuotaWarning';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../hooks/useAuth';
import { useAlert } from '../../contexts/AlertContext';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import styles from './CreateShopPage.module.css';

const CreateShopPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLocalization();
  const { success, error } = useAlert();
  const { settings: messageCenterSettings } = useBusinessSettings('messageCenter');
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    printableName: '',
    type: 'online',
    orderNumberPrefix: '',
    address: '',
    phone: '',
    logo: null,
    deliveryCharges: [],
    serviceCharge: '',
    allowCreditSales: false,
    cashierSessionRequired: false,
    messageCenterShiftEligible: false,
  });
  
  const [shippingZones, setShippingZones] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingZones, setIsLoadingZones] = useState(true);
  const [isCheckingPrefix, setIsCheckingPrefix] = useState(false);
  const [prefixStatus, setPrefixStatus] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [showExtraCharges, setShowExtraCharges] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [isLoadingQuota, setIsLoadingQuota] = useState(true);

  // Shop types
  const shopTypes = [
    { value: 'online', label: t('shops.online'), icon: Store },
    { value: 'pos', label: t('shops.pos'), icon: Package },
    { value: 'dropshipper', label: t('shops.dropshipper'), icon: Truck }
  ];

  // Fetch shipping zones on component mount
  useEffect(() => {
    const fetchShippingZones = async () => {
      try {
        setIsLoadingZones(true);
        const response = await getShippingZones();
        setShippingZones(response);
        
        // Initialize delivery charges for all zones
        const initialCharges = response.map(zone => ({
          shippingZoneId: zone.id,
          amount: '',
          extraChargePerKg: ''
        }));
        setFormData(prev => ({
          ...prev,
          deliveryCharges: initialCharges
        }));
      } catch (error) {
        console.error('Failed to fetch shipping zones:', error);
        error(t('shops.failedToLoadZones'), {
          title: t('errors.loadingError'),
          duration: 5000
        });
      } finally {
        setIsLoadingZones(false);
      }
    };

    fetchShippingZones();
  }, [t, error]);

  // Debounced prefix validation
  useEffect(() => {
    if (!formData.orderNumberPrefix || formData.orderNumberPrefix.length < 2) {
      setPrefixStatus(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsCheckingPrefix(true);
        const response = await checkOrderPrefix(formData.orderNumberPrefix);
        setPrefixStatus(response.taken ? 'taken' : 'available');
      } catch (error) {
        console.error('Failed to check prefix:', error);
        setPrefixStatus(null);
      } finally {
        setIsCheckingPrefix(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.orderNumberPrefix]);

  useEffect(() => {
    const fetchQuota = async () => {
      try {
        setIsLoadingQuota(true);
        const response = await getMyUsage();
        setQuotaInfo(response.usage?.shops || null);
      } catch (err) {
        console.error('Failed to fetch quota:', err);
      } finally {
        setIsLoadingQuota(false);
      }
    };

    fetchQuota();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = t('shops.nameRequired');
    }

    if (!formData.type) {
      newErrors.type = t('shops.typeRequired');
    }

    if (!formData.orderNumberPrefix.trim()) {
      newErrors.orderNumberPrefix = t('shops.prefixRequired');
    } else if (formData.orderNumberPrefix.length < 2) {
      newErrors.orderNumberPrefix = t('shops.prefixMinLength');
    } else if (prefixStatus === 'taken') {
      newErrors.orderNumberPrefix = t('shops.prefixTaken');
    }

    // Phone validation (if provided)
    if (formData.phone && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = t('shops.invalidPhone');
    }

    // Delivery charges validation - only for online and dropshipper shops
    if (formData.type === 'online' || formData.type === 'dropshipper') {
      const hasEmptyCharges = formData.deliveryCharges.some(charge => 
        charge.amount === '' || charge.amount < 0
      );
      if (hasEmptyCharges) {
        newErrors.deliveryCharges = t('shops.deliveryChargesRequired');
      }
    }

    // Extra charges per KG validation
    if (showExtraCharges && (formData.type === 'online' || formData.type === 'dropshipper')) {
      const hasInvalidExtraCharges = formData.deliveryCharges.some(charge =>
        charge.extraChargePerKg === '' || isNaN(charge.extraChargePerKg) || parseFloat(charge.extraChargePerKg) < 0
      );
      if (hasInvalidExtraCharges) {
        newErrors.extraCharges = t('shops.extraChargesRequired');
      }
    }

    // Service charge validation for dropshippers
    if (formData.type === 'dropshipper') {
      if (!formData.serviceCharge || formData.serviceCharge === '') {
        newErrors.serviceCharge = t('shops.serviceChargeRequired');
      } else if (isNaN(formData.serviceCharge) || formData.serviceCharge < 0) {
        newErrors.serviceCharge = t('shops.invalidServiceCharge');
      }
    }

    // Logo validation (if provided)
    if (formData.logo) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(formData.logo.type)) {
        newErrors.logo = t('shops.invalidLogoFormat');
      } else if (formData.logo.size > 5 * 1024 * 1024) { // 5MB
        newErrors.logo = t('shops.logoTooLarge');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      type,
      serviceCharge: type === 'dropshipper' ? prev.serviceCharge : ''
    }));
    
    if (errors.type) {
      setErrors(prev => ({
        ...prev,
        type: ''
      }));
    }
  };

  const handleDeliveryChargeChange = (zoneId, amount) => {
    setFormData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge =>
        charge.shippingZoneId === zoneId
          ? { ...charge, amount: amount === '' ? '' : parseFloat(amount) }
          : charge
      )
    }));

    if (errors.deliveryCharges) {
      setErrors(prev => ({
        ...prev,
        deliveryCharges: ''
      }));
    }
  };

  const handleExtraChargeChange = (zoneId, value) => {
    setFormData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge =>
        charge.shippingZoneId === zoneId
          ? { ...charge, extraChargePerKg: value === '' ? '' : value }
          : charge
      )
    }));

    if (errors.extraCharges) {
      setErrors(prev => ({ ...prev, extraCharges: '' }));
    }
  };

  const handleBulkEditOpen = () => {
    setBulkEditValue('');
    setShowBulkEditModal(true);
  };

  const handleBulkEditApply = () => {
    if (bulkEditValue === '' || isNaN(bulkEditValue) || parseFloat(bulkEditValue) < 0) {
      error(t('shops.invalidBulkEditValue'));
      return;
    }

    setFormData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge => ({
        ...charge,
        amount: parseFloat(bulkEditValue)
      }))
    }));

    if (errors.deliveryCharges) {
      setErrors(prev => ({
        ...prev,
        deliveryCharges: ''
      }));
    }

    setShowBulkEditModal(false);
    success(t('shops.bulkEditApplied'));
  };

  const handleSetFreeDeliveryAll = () => {
    setFormData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge => ({
        ...charge,
        amount: 0
      }))
    }));

    if (errors.deliveryCharges) {
      setErrors(prev => ({
        ...prev,
        deliveryCharges: ''
      }));
    }

    success(t('shops.freeDeliveryApplied'));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        logo: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }

    if (errors.logo) {
      setErrors(prev => ({
        ...prev,
        logo: ''
      }));
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo: null
    }));
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getQuotaSeverity = () => {
    if (!quotaInfo || quotaInfo.isUnlimited) return 'info';
    const percentage = quotaInfo.limit > 0 ? (quotaInfo.usage / quotaInfo.limit) * 100 : 0;
    if (percentage >= 90) return 'critical';
    if (percentage >= 80) return 'warning';
    return 'info';
  };

  const isQuotaExceeded = () => {
    if (!quotaInfo || quotaInfo.isUnlimited) return false;
    return quotaInfo.usage >= quotaInfo.limit;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      // Prepare form data for multipart/form-data
      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      if (formData.printableName.trim()) {
        submitData.append('printableName', formData.printableName.trim());
      }
      submitData.append('type', formData.type);
      submitData.append('orderNumberPrefix', formData.orderNumberPrefix.trim());
      
      if (formData.address.trim()) {
        submitData.append('address', formData.address.trim());
      }
      
      if (formData.phone.trim()) {
        submitData.append('phone', formData.phone.trim());
      }
      
      if (formData.logo) {
        submitData.append('logo', formData.logo);
      }

      // Filter out empty delivery charges and convert to JSON
      const validDeliveryCharges = formData.deliveryCharges
        .filter(charge => charge.amount !== '' && charge.amount >= 0)
        .map(charge => ({
          shippingZoneId: charge.shippingZoneId,
          amount: parseFloat(charge.amount),
          extraChargePerKg: showExtraCharges ? (parseFloat(charge.extraChargePerKg) || 0) : 0,
        }));
      submitData.append('deliveryCharges', JSON.stringify(validDeliveryCharges));

      if (formData.type === 'dropshipper' && formData.serviceCharge) {
        submitData.append('serviceCharge', parseFloat(formData.serviceCharge));
      }

      if (formData.type === 'pos') {
        submitData.append('allowCreditSales', formData.allowCreditSales);
        submitData.append('cashierSessionRequired', formData.cashierSessionRequired);
      }

      if (formData.type !== 'pos') {
        submitData.append('messageCenterShiftEligible', formData.messageCenterShiftEligible);
      }

      const response = await createShop(submitData);
      success(t('shops.shopCreatedSuccess', { shopName: response.shop.name }), {
        title: t('common.success'),
        duration: 4000
      });
      navigate('/shops');
    } catch (error) {
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      } else {
        setErrors({ submit: t('shops.failedToCreate') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingZones) {
    return (
      <div className={styles.createShopPage}>
        <PageHeader
          title={t('shops.createNewShop')}
          description={t('shops.setupYourShop')}
          icon={Store}
          actions={
            <button 
              onClick={() => navigate('/shops')} 
              className={styles.backButton}
            >
              <ArrowLeft size={20} />
              {t('shops.backToShops')}
            </button>
          }
        />

        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('shops.loadingData')}</h3>
            <p>{t('shops.loadingDataDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createShopPage}>
      <PageHeader
        title={t('shops.createNewShop')}
        description={t('shops.setupYourShop')}
        icon={Store}
        actions={
          <button 
            onClick={() => navigate('/shops')} 
            className={styles.backButton}
          >
            <ArrowLeft size={20} />
            {t('shops.backToShops')}
          </button>
        }
      />

      <div className={styles.pageContent}>
        {!isLoadingQuota && quotaInfo && (
          <QuotaWarning
            resourceType="shops"
            usage={quotaInfo}
            severity={getQuotaSeverity()}
            dismissible={!isQuotaExceeded()}
          />
        )}
        {errors.submit && (
          <div className={styles.errorBanner}>
            <AlertCircle size={20} />
            <div>
              <p>{errors.submit}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.shopForm}>
          {/* Basic Information */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <h2>{t('shops.basicInformation')}</h2>
              <p>{t('shops.basicInformationDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="name">{t('shops.shopName')} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('shops.shopNamePlaceholder')}
                    className={errors.name ? 'error' : ''}
                    required
                  />
                  {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="printableName">{t('shops.printableName')}</label>
                  <input
                    type="text"
                    id="printableName"
                    name="printableName"
                    value={formData.printableName}
                    onChange={handleInputChange}
                    placeholder={t('shops.printableNamePlaceholder')}
                  />
                  <span className={styles.formHint}>{t('shops.printableNameHint')}</span>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="orderNumberPrefix">{t('shops.orderPrefix')} *</label>
                  <div className={styles.prefixInputWrapper}>
                    <input
                      type="text"
                      id="orderNumberPrefix"
                      name="orderNumberPrefix"
                      value={formData.orderNumberPrefix}
                      onChange={handleInputChange}
                      placeholder="ABC"
                      className={`${styles.prefixInput} ${errors.orderNumberPrefix ? 'error' : ''} ${prefixStatus === 'available' ? 'success' : ''}`}
                      maxLength="5"
                      style={{ textTransform: 'uppercase' }}
                      onInput={(e) => e.target.value = e.target.value.toUpperCase()}
                      required
                    />
                    <div className={styles.prefixStatus}>
                      {isCheckingPrefix && <div className={styles.checkingSpinner}></div>}
                      {!isCheckingPrefix && prefixStatus === 'available' && (
                        <Check size={16} className={`${styles.statusIcon} ${styles.success}`} />
                      )}
                      {!isCheckingPrefix && prefixStatus === 'taken' && (
                        <X size={16} className={`${styles.statusIcon} ${styles.error}`} />
                      )}
                    </div>
                  </div>
                  {errors.orderNumberPrefix && <span className={styles.errorText}>{errors.orderNumberPrefix}</span>}
                  {!errors.orderNumberPrefix && prefixStatus === 'available' && (
                    <span className={styles.successText}>{t('shops.prefixAvailable')}</span>
                  )}
                  <span className={styles.formHint}>{t('shops.prefixHint')}</span>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>{t('shops.shopType')} *</label>
                <div className={styles.shopTypeGrid}>
                  {shopTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        className={`${styles.shopTypeCard} ${formData.type === type.value ? styles.selected : ''}`}
                        onClick={() => handleTypeChange(type.value)}
                      >
                        <Icon size={24} className={styles.typeIcon} />
                        <span className={styles.typeLabel}>{type.label}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.type && <span className={styles.errorText}>{errors.type}</span>}
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="address">{t('shops.address')}</label>
                  <div className={styles.inputWithIcon}>
                    <MapPin size={18} className={styles.inputIcon} />
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder={t('shops.addressPlaceholder')}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="phone">{t('shops.phone')}</label>
                  <div className={styles.inputWithIcon}>
                    <Phone size={18} className={styles.inputIcon} />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('shops.phonePlaceholder')}
                      className={errors.phone ? 'error' : ''}
                    />
                  </div>
                  {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Logo Upload */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <h2>{t('shops.shopLogo')}</h2>
              <p>{t('shops.logoDesc')}</p>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.logoUploadSection}>
                {!logoPreview ? (
                  <div className={styles.logoUploadArea} onClick={() => fileInputRef.current?.click()}>
                    <Upload size={32} className={styles.uploadIcon} />
                    <p className={styles.uploadText}>{t('shops.uploadLogo')}</p>
                    <p className={styles.uploadHint}>{t('shops.logoFormats')}</p>
                  </div>
                ) : (
                  <div className={styles.logoPreview}>
                    <img src={logoPreview} alt="Logo preview" className={styles.previewImage} />
                    <button
                      type="button"
                      className={styles.removeLogoButton}
                      onClick={handleRemoveLogo}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={handleLogoChange}
                  style={{ display: 'none' }}
                />
                
                {errors.logo && <span className={styles.errorText}>{errors.logo}</span>}
              </div>
            </div>
          </div>

          {/* Delivery Charges - Only show for online and dropshipper shops */}
          {(formData.type === 'online' || formData.type === 'dropshipper') && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <div className={styles.cardHeaderWithActions}>
                  <div>
                    <h2>{t('shops.deliveryCharges')}</h2>
                    <p>{t('shops.deliveryChargesDesc')}</p>
                  </div>
                  <div className={styles.bulkActions}>
                    <button
                      type="button"
                      onClick={handleBulkEditOpen}
                      className={styles.bulkEditButton}
                    >
                      <Edit3 size={16} />
                      {t('shops.bulkEdit')}
                    </button>
                    <button
                      type="button"
                      onClick={handleSetFreeDeliveryAll}
                      className={styles.freeDeliveryButton}
                    >
                      <Package size={16} />
                      {t('shops.setFreeDeliveryAll')}
                    </button>
                  </div>
                </div>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.deliveryChargesGrid}>
                  {shippingZones.map((zone) => {
                    const charge = formData.deliveryCharges.find(c => c.shippingZoneId === zone.id);
                    return (
                      <div key={zone.id} className={styles.chargeInputGroup}>
                        <label htmlFor={`charge-${zone.id}`}>
                          {zone.name} <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.inputWithIcon}>
                          <Banknote size={18} className={styles.inputIcon} />
                          <input
                            type="number"
                            id={`charge-${zone.id}`}
                            value={charge?.amount === 0 ? 0 : (charge?.amount || '')}
                            onChange={(e) => handleDeliveryChargeChange(zone.id, e.target.value)}
                            placeholder="150.00"
                            min="0"
                            step="0.01"
                            className={`${styles.deliveryInput} ${errors.deliveryCharges ? 'error' : ''}`}
                            required
                          />
                          <span className={styles.currencyLabel}>{user?.business?.currency || null}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {errors.deliveryCharges && <span className={styles.errorText}>{errors.deliveryCharges}</span>}

                {/* Extra charges checkbox */}
                <div className={styles.extraChargesToggle}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={showExtraCharges}
                      onChange={(e) => {
                        setShowExtraCharges(e.target.checked);
                        if (errors.extraCharges) setErrors(prev => ({ ...prev, extraCharges: '' }));
                      }}
                      className={styles.checkbox}
                    />
                    <span>{t('shops.setupExtraCharges')}</span>
                  </label>
                </div>

                {/* Extra charges per KG fields */}
                {showExtraCharges && (
                  <div className={styles.extraChargesSection}>
                    <p className={styles.extraChargesDesc}>{t('shops.extraChargesDesc')}</p>
                    <div className={styles.deliveryChargesGrid}>
                      {shippingZones.map((zone) => {
                        const charge = formData.deliveryCharges.find(c => c.shippingZoneId === zone.id);
                        return (
                          <div key={zone.id} className={styles.chargeInputGroup}>
                            <label htmlFor={`extra-${zone.id}`}>
                              {zone.name} <span className={styles.required}>*</span>
                            </label>
                            <div className={styles.inputWithIcon}>
                              <Banknote size={18} className={styles.inputIcon} />
                              <input
                                type="number"
                                id={`extra-${zone.id}`}
                                value={charge?.extraChargePerKg === 0 ? 0 : (charge?.extraChargePerKg || '')}
                                onChange={(e) => handleExtraChargeChange(zone.id, e.target.value)}
                                placeholder={t('shops.extraChargePerKgPlaceholder')}
                                min="0"
                                step="0.01"
                                className={`${styles.deliveryInput} ${errors.extraCharges ? 'error' : ''}`}
                              />
                              <span className={styles.currencyLabel}>{user?.business?.currency || null}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {errors.extraCharges && <span className={styles.errorText}>{errors.extraCharges}</span>}
                  </div>
                )}

                <div className={styles.deliveryHint}>
                  <p>{t('shops.deliveryChargesHint')}</p>
                </div>
              </div>
            </div>
          )}

          {/* POS Settings */}
          {formData.type === 'pos' && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>{t('shops.posSettings')}</h2>
                <p>{t('shops.posSettingsDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.allowCreditSales}
                      onChange={(e) => setFormData(prev => ({ ...prev, allowCreditSales: e.target.checked }))}
                      className={styles.checkbox}
                    />
                    <span>{t('shops.allowCreditSales')}</span>
                  </label>
                  <span className={styles.formHint}>{t('shops.allowCreditSalesHint')}</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.cashierSessionRequired}
                      onChange={(e) => setFormData(prev => ({ ...prev, cashierSessionRequired: e.target.checked }))}
                      className={styles.checkbox}
                    />
                    <span>{t('shops.cashierSessionRequired')}</span>
                  </label>
                  <span className={styles.formHint}>{t('shops.cashierSessionRequiredHint')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Message Center Shift Settings — online/dropshipper shops, only when the business has the feature enabled */}
          {formData.type !== 'pos' && messageCenterSettings.enabled && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>{t('shops.messageCenterShiftSettings')}</h2>
                <p>{t('shops.messageCenterShiftSettingsDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.formGroup}>
                  <div className={styles.toggleRow}>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        checked={formData.messageCenterShiftEligible}
                        onChange={(e) => setFormData(prev => ({ ...prev, messageCenterShiftEligible: e.target.checked }))}
                      />
                      <span className={styles.slider} />
                    </label>
                    <span>{t('shops.messageCenterShiftEligible')}</span>
                  </div>
                  <span className={styles.formHint}>{t('shops.messageCenterShiftEligibleHint')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Service Charge (for dropshippers) */}
          {formData.type === 'dropshipper' && (
            <div className={styles.formCard}>
              <div className={styles.cardHeader}>
                <h2>{t('shops.serviceCharge')}</h2>
                <p>{t('shops.serviceChargeDesc')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.formGroup}>
                  <label htmlFor="serviceCharge">{t('shops.serviceChargeAmount')} *</label>
                  <div className={styles.inputWithIcon}>
                    <Banknote size={18} className={styles.inputIcon} />
                    <input
                      type="number"
                      id="serviceCharge"
                      name="serviceCharge"
                      value={formData.serviceCharge}
                      onChange={handleInputChange}
                      placeholder="50.00"
                      min="0"
                      step="0.01"
                      className={errors.serviceCharge ? 'error' : ''}
                      required
                    />
                    <span className={styles.currencyLabel}>{user?.business?.currency || null}</span>
                  </div>
                  {errors.serviceCharge && <span className={styles.errorText}>{errors.serviceCharge}</span>}
                  <span className={styles.formHint}>{t('shops.serviceChargeHint')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className={styles.formActions}>
            <button 
              type="button" 
              onClick={() => navigate('/shops')}
              className={styles.cancelButton}
            >
              <X size={18} />
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isLoading || isCheckingPrefix || prefixStatus === 'taken' || isQuotaExceeded()}
            >
              {isLoading ? (
                <div className={styles.loadingSpinner}></div>
              ) : (
                <>
                  <Save size={18} />
                  {t('shops.createShop')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <Edit3 size={24} />
              <h3>{t('shops.bulkEditDeliveryCharges')}</h3>
              <button onClick={() => setShowBulkEditModal(false)} className={styles.closeButton}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.dialogBody}>
              <div className={styles.formGroup}>
                <label htmlFor="bulkEditValue">{t('shops.deliveryChargeAmount')}</label>
                <div className={styles.inputWithIcon}>
                  <Banknote size={18} className={styles.inputIcon} />
                  <input
                    type="number"
                    id="bulkEditValue"
                    value={bulkEditValue}
                    onChange={(e) => setBulkEditValue(e.target.value)}
                    placeholder="150.00"
                    min="0"
                    step="0.01"
                    className={styles.input}
                    autoFocus
                  />
                  <span className={styles.currencyLabel}>{user?.business?.currency || null}</span>
                </div>
                <span className={styles.formHint}>{t('shops.bulkEditHint')}</span>
              </div>
            </div>
            <div className={styles.dialogActions}>
              <button
                type="button"
                onClick={() => setShowBulkEditModal(false)}
                className={styles.cancelButton}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleBulkEditApply}
                className={styles.submitButton}
              >
                <Check size={16} />
                {t('shops.applyToAll')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateShopPage;